import { getUtms } from './utm';
import { LOCALE_CANDIDATES, normalizeLocale } from './locale';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL;
const STRAPI_API_URL = import.meta.env.VITE_STRAPI_API_URL;
const DEFAULT_POPULATE = '*';
const LEAD_SOURCE_VALUES = new Set(['instagram', 'facebook', 'web', 'referido', 'phone', 'email', 'otro']);

const normalizeMediaText = (value) => (
  typeof value === 'string' ? value.trim() : ''
);

const getMediaField = (media, field) => {
  if (!media || typeof media === 'string') return '';

  return normalizeMediaText(
    media[field]
    ?? media?.data?.[field]
    ?? media?.data?.attributes?.[field]
  );
};

const normalizeLeadSource = (value) => {
  const normalized = String(value || '').trim().toLowerCase();

  if (!normalized) return 'web';
  if (LEAD_SOURCE_VALUES.has(normalized)) return normalized;
  if (normalized === 'website' || normalized === 'sitio web' || normalized.startsWith('/')) return 'web';
  if (normalized.includes('instagram')) return 'instagram';
  if (normalized.includes('facebook') || normalized.includes('meta')) return 'facebook';
  if (normalized.includes('refer')) return 'referido';
  if (normalized.includes('phone') || normalized.includes('telefono') || normalized.includes('tel')) return 'phone';
  if (normalized.includes('mail') || normalized.includes('correo') || normalized.includes('email')) return 'email';

  return 'web';
};

// Slugs que NO son paginas de propiedad/tipologia: home neutral y paginas funcionales.
const NON_PROPERTY_SLUGS = new Set(['home', 'inicio', 'index', 'homeeng', 'contacto']);

// Extrae el slug de la pagina de tipologia/propiedad del array dynamic_page de un item.
// La relacion es manyToMany: un item puede estar en home + contacto + su tipologia.
// Filtra los slugs no-propiedad y devuelve el primero util. '' si no hay pagina de propiedad.
export const resolveItemPageSlug = (item) => {
  const pages = item?.dynamic_page;
  if (!pages) return '';
  const arr = Array.isArray(pages) ? pages : [pages];
  const match = arr.find((p) => {
    const slug = String(p?.slug || '').trim().toLowerCase();
    return slug && !NON_PROPERTY_SLUGS.has(slug);
  });
  return match?.slug || '';
};

// Construye una URL publica limpia para la propiedad de interes del lead.
// Ej: buildPropiedadLink('of-2personas', 'OF-2101', 'GuillermoJackson5106')
//   -> 'https://www.wework.koppi.mx/of-2personas?unidad=OF-2101&smartlink=GuillermoJackson5106'
// Requiere el slug de la pagina del item (resolveItemPageSlug), NO el slug del form.
// Sin utms/locale: link estable para mandar por mail. Devuelve '' si no hay unidad o slug.
export const buildPropiedadLink = (slug, unidad, smartlink = '') => {
  const cleanUnidad = String(unidad || '').trim();
  const cleanSlug = String(slug || '').replace(/^\/+/, '').replace(/\/+$/, '');
  if (!cleanUnidad || !cleanSlug) return '';
  const siteUrl = String(import.meta.env.VITE_SITE_URL || '').replace(/\/+$/, '');
  const origin = siteUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  if (!origin) return '';
  let link = `${origin}/${cleanSlug}?unidad=${encodeURIComponent(cleanUnidad)}`;
  if (smartlink) link += `&smartlink=${encodeURIComponent(smartlink)}`;
  return link;
};

const getDynamicPagePopulateParams = () => ({
  'populate[seo]': 'true',
  'populate[localizations][fields][0]': 'slug',
  'populate[localizations][fields][1]': 'locale',
  'populate[seo][populate][metaImage][fields][0]': 'url',
  'populate[seo][populate][metaImage][fields][1]': 'alternativeText',
  'populate[seo][populate][metaImage][fields][2]': 'caption',
  'populate[seo][populate][metaImage][fields][3]': 'width',
  'populate[seo][populate][metaImage][fields][4]': 'height',
  'populate[seo][populate][metaImage][fields][5]': 'mime',
  'populate[hero][populate]': '*',
  'populate[items][populate][features]': '*',
  'populate[items][populate][dynamic_page][fields][0]': 'slug',
  'populate[sections][populate]': '*',
  'populate[sections][on][blocks.scroll-cta][populate]': '*',
  'populate[sections][on][blocks.espacio-vacio][populate]': '*',
  'populate[sections][on][blocks.contact-form][populate][backgroundImage]': 'true',
  'populate[sections][on][blocks.contact-form][populate][fields][populate]': '*',
  'populate[sections][on][blocks.contact-form][populate][content][populate]': '*',
  'populate[sections][on][blocks.contact-form][populate][thankYou][populate]': '*',
  'populate[sections][on][blocks.cotizador][populate][imagen]': 'true',
  'populate[sections][on][blocks.cotizador][populate][campos][populate]': '*',
  'populate[sections][on][blocks.cotizador][populate][esquemasDePago][populate]': '*',
  'populate[sections][on][blocks.cotizador][populate][extras][populate]': '*',
  'populate[sections][on][blocks.description][populate][icon]': 'true',
  'populate[sections][on][blocks.description][populate][content][populate]': '*',
  'populate[sections][on][blocks.descripcion-home][populate][backgroundImage]': 'true',
  'populate[sections][on][blocks.descripcion-home][populate][icon]': 'true',
  'populate[sections][on][blocks.descripcion-home][populate][content][populate]': '*',
  'populate[sections][on][blocks.descripcion-modelo][populate][icon]': 'true',
  'populate[sections][on][blocks.descripcion-modelo][populate][content][populate]': '*',
  'populate[sections][on][blocks.floorplan][populate][backgroundImage]': 'true',
  'populate[sections][on][blocks.floorplan][populate][galeria][populate]': '*',
  'populate[sections][on][blocks.gallery][populate][items][populate]': '*',
  'populate[sections][on][blocks.gallery-info][populate][items][populate]': '*',
  'populate[sections][on][blocks.hero-gallery][populate]': '*',
  'populate[sections][on][blocks.hero-iframe360][populate]': '*',
  'populate[sections][on][blocks.hero-image][populate]': '*',
  'populate[sections][on][blocks.hero-secciones][populate]': '*',
  'populate[sections][on][blocks.hero-video][populate]': '*',
  'populate[sections][on][blocks.popup-fullscreen][populate][media]': 'true',
  'populate[sections][on][blocks.popup-fullscreen][populate][posterImage]': 'true',
  'populate[sections][on][blocks.legal-text][populate]': '*',
  'populate[sections][on][blocks.location][populate]': '*',
  'populate[sections][on][blocks.main-features][populate][features][populate]': '*',
  'populate[sections][on][blocks.model-header][populate]': '*',
  'populate[sections][on][blocks.secondary-features][populate][features][populate]': '*',
  'populate[sections][on][blocks.availability-list][populate]': '*',
  'populate[sections][on][blocks.tipologias][populate][cards][populate]': '*',
});

export const getStrapiMedia = (media) => {
  if (!media) return null;

  const url = typeof media === 'string'
    ? media
    : media?.url || media?.data?.url || media?.data?.attributes?.url;

  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${STRAPI_URL}${url}`;
};

export const getStrapiMediaAttributes = (media, options = {}) => {
  const { altFallback = '', titleFallback = '', decorative = false } = options;
  const src = getStrapiMedia(media);

  if (!src) {
    return {
      src: null,
      alt: decorative ? '' : normalizeMediaText(altFallback),
      title: decorative ? undefined : normalizeMediaText(titleFallback) || undefined,
    };
  }

  if (decorative) {
    return { src, alt: '', title: undefined };
  }

  const alt = getMediaField(media, 'alternativeText') || normalizeMediaText(altFallback);
  const title = getMediaField(media, 'caption') || normalizeMediaText(titleFallback) || undefined;

  return { src, alt, title };
};

export const fetchStrapi = async (endpoint, params = {}, options = {}) => {
  const { skipPopulate = false, suppressErrors = false } = options;
  const normalizedParams = { ...params };

  if (!skipPopulate && normalizedParams.populate == null) {
    normalizedParams.populate = DEFAULT_POPULATE;
  }

  const cleanParams = Object.fromEntries(
    Object.entries(normalizedParams).filter(([, v]) => v != null && v !== '')
  );
  const queryString = new URLSearchParams(cleanParams).toString();
  const url = `${STRAPI_API_URL}${endpoint}${queryString ? `?${queryString}` : ''}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      // Suppress noisy console errors for 404s — these are expected for
      // section aliases (e.g. /amenidades) that don't have a real page.
      if (!suppressErrors && response.status !== 404) {
        const errorText = await response.text();
        console.error('Strapi error response:', errorText);
      }
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    if (!suppressErrors && !error?.message?.includes('404')) {
      console.error('Error fetching Strapi:', error);
    }
    throw error;
  }
};

export const getGlobalData = async (locale) => {
  const fetchSafe = async (endpoint, params = {}, options = {}) => {
    try {
      return await fetchStrapi(endpoint, params, options);
    } catch (error) {
      console.warn(`Failed to fetch ${endpoint}:`, error.message);
      return { data: null };
    }
  };

  const baseParams = {};
  if (locale) {
    baseParams.locale = normalizeLocale(locale);
  }

  const headerParams = {
    ...baseParams,
    'populate[navigation][populate][children]': '*',
    'populate[ctaButton]': '*',
    'populate[contactMenu][populate][items]': '*',
    'populate[logo][fields][0]': 'url',
    'populate[logo][fields][1]': 'alternativeText',
    'populate[logo][fields][2]': 'width',
    'populate[logo][fields][3]': 'height',
    'populate[logo][fields][4]': 'caption',
  };

  const footerParams = {
    ...baseParams,
    'populate[logo]': 'true',
    'populate[creatorLogo]': 'true',
    'populate[navigation]': 'true',
    'populate[legalLinks]': 'true',
  };

  const [companyInfo, header, footer, themeConfig] = await Promise.all([
    fetchSafe('/company-info', baseParams, { skipPopulate: true }),
    fetchSafe('/header', headerParams, { skipPopulate: true }),
    fetchSafe('/footer', footerParams, { skipPopulate: true }),
    fetchSafe('/theme-config', baseParams),
  ]);

  return {
    companyInfo: companyInfo?.data,
    header: header?.data,
    footer: footer?.data,
    themeConfig: themeConfig?.data,
  };
};

export const getAvailableLocales = async () => {
  // Verify which configured locales actually have published company-info content
  const candidates = LOCALE_CANDIDATES;

  const checks = await Promise.all(
    candidates.map(async (loc) => {
      try {
        const res = await fetchStrapi('/company-info', { locale: loc }, { skipPopulate: true });
        return res?.data ? loc : null;
      } catch {
        return null;
      }
    })
  );

  return checks.filter(Boolean);
};

export const getPriceMap = async (locale) => {
  const params = {};
  if (locale) params.locale = normalizeLocale(locale);

  try {
    const response = await fetchStrapi('/dynamic-pages/price-map', params, { skipPopulate: true });
    return response?.data || {};
  } catch (error) {
    console.warn('Failed to fetch price map:', error.message);
    return {};
  }
};

export const getLegalRouteMap = async (locale) => {
  const params = {};
  if (locale) params.locale = normalizeLocale(locale);

  try {
    const response = await fetchStrapi('/dynamic-pages/legal-link-map', params, { skipPopulate: true });
    const routeMap = response?.data || {};

    return Object.fromEntries(
      Object.entries(routeMap).map(([path, entry]) => [
        path,
        {
          ...entry,
          fileUrl: entry?.fileUrl ? getStrapiMedia(entry.fileUrl) : null,
        },
      ])
    );
  } catch (error) {
    console.warn('Failed to fetch legal route map:', error.message);
    return {};
  }
};

export const getPageBySlug = async (slug, locale) => {
  const params = {};
  if (locale) {
    params.locale = normalizeLocale(locale);
  }

  const pageParams = {
    ...params,
    'filters[slug][$eq]': slug,
    ...getDynamicPagePopulateParams(),
  };

  try {
    const response = await fetchStrapi('/dynamic-pages', pageParams, {
      skipPopulate: true,
      suppressErrors: true,
    });
    return response?.data?.[0] || null;
  } catch (error) {
    // 404 just means the slug isn't a real page — let the caller decide
    // (e.g. fall back to the home page for section aliases like /amenidades).
    if (error?.message?.includes('404')) return null;
    throw error;
  }
};

export const getAllPages = async (locale) => {
  const params = {
    'fields[0]': 'slug',
    'fields[1]': 'titulo',
  };

  if (locale) {
    params.locale = normalizeLocale(locale);
  }

  const response = await fetchStrapi('/dynamic-pages', params, { skipPopulate: true });

  return response?.data || [];
};

export const getPopupFullscreenEntries = async (locale) => {
  const params = {
    'fields[0]': 'slug',
    'fields[1]': 'titulo',
    'fields[2]': 'documentId',
    'pagination[pageSize]': 100,
    'populate[sections][on][blocks.popup-fullscreen][populate][media]': 'true',
    'populate[sections][on][blocks.popup-fullscreen][populate][posterImage]': 'true',
  };

  if (locale) {
    params.locale = normalizeLocale(locale);
  }

  try {
    const response = await fetchStrapi('/dynamic-pages', params, { skipPopulate: true });
    const pages = response?.data || [];

    return pages.flatMap((page) => (page.sections || [])
      .filter((section) => section?.__component === 'blocks.popup-fullscreen')
      .map((section) => ({
        ...section,
        pageSlug: page.slug,
        pageTitle: page.titulo,
        pageDocumentId: page.documentId,
      })));
  } catch (error) {
    console.warn('Failed to fetch popup fullscreen entries:', error.message);
    return [];
  }
};

export const submitLead = async (formData) => {
  const url = `${STRAPI_API_URL}/leads/submit`;

  const utms = getUtms(typeof window !== 'undefined' ? window.location.search : '');
  const {
    fullName,
    fullname,
    name,
    email,
    phone,
    country,
    estimatedValue,
    source,
    leadType,
    data,
    campaign,
    promocion,
    IdProducto,
    projectId,
    asesorEmail,
    asesorName,
    propiedadLink,
  } = formData || {};

  const resolvedCampaign = campaign || utms.utm_campaign || undefined;

  const nestedData = {
    ...(data || {}),
    ...(leadType ? { leadType } : {}),
    ...(source ? { originalSource: source } : {}),
    ...(Object.keys(utms).length ? { utms } : {}),
  };

  const payload = {
    data: {
      name: name || fullName || fullname || '',
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
      ...(country ? { country } : {}),
      ...(estimatedValue != null ? { estimatedValue } : {}),
      source: normalizeLeadSource(source),
      ...(resolvedCampaign ? { campaign: resolvedCampaign } : {}),
      ...(promocion ? { promocion } : {}),
      ...(IdProducto ? { IdProducto } : {}),
      ...(projectId ? { projectId } : {}),
      ...(asesorEmail && asesorEmail !== '-' ? { asesorEmail } : {}),
      ...(asesorName ? { asesorName } : {}),
      ...(propiedadLink ? { propiedadLink } : {}),
      ...(typeof window !== 'undefined' && window.location.href ? { url: window.location.href } : {}),
      ...(Object.keys(nestedData).length ? { data: nestedData } : {}),
    },
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error submitting lead:', errorText);
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error submitting lead:', error);
    throw error;
  }
};

export const submitCotizacion = async (cotizacionData) => {
  const url = `${STRAPI_API_URL}/cotizaciones`;
  const utms = getUtms(typeof window !== 'undefined' ? window.location.search : '');

  const { asesorEmail, asesorName, propiedadLink, ...rest } = cotizacionData || {};

  const payload = {
    ...rest,
    ...(asesorEmail && asesorEmail !== '-' ? { asesorEmail } : {}),
    ...(asesorName ? { asesorName } : {}),
    ...(propiedadLink ? { propiedadLink } : {}),
    campaign: cotizacionData?.campaign || utms.utm_campaign || undefined,
    info: {
      ...(cotizacionData?.info || {}),
      utms: Object.keys(utms).length ? utms : undefined,
    },
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: payload }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error submitting cotizacion:', errorText);
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error submitting cotizacion:', error);
    throw error;
  }
};

export default {
  fetchStrapi,
  getStrapiMedia,
  getGlobalData,
  getPageBySlug,
  getAllPages,
  getPopupFullscreenEntries,
  getLegalRouteMap,
  submitLead,
  submitCotizacion,
};
